/**
 * @fileoverview Canonical Precedent Field Definitions
 *
 * The single source of truth for all field keys used across precedent templates.
 * Every field extracted by Sonnet must map to one of these canonical keys.
 * Users configure default values against these keys in the Fields settings tab.
 *
 * Rules:
 * - Every field key must be unique across all categories
 * - Keys use snake_case
 * - New fields are added here first, then become available everywhere
 * - AI extraction is instructed to normalize to these keys
 */

import type { AppointmentType } from "@cvx/jobs/schema";
import {
  isBankruptcyAppointmentType,
  isPersonalInsolvencyAppointmentType,
} from "@cvx/lib/appointmentTypes";
import { SCOPED_TITLE_KEYS, TITLE_KEYS } from "@cvx/lib/titleScopes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FieldType =
  | "text"
  | "date"
  | "address"
  | "signature"
  | "checkbox"
  | "number"
  | "email"
  | "phone";

export interface CanonicalField {
  /** Unique snake_case key — used as the canonical identifier everywhere */
  key: string;
  /** Human-readable label */
  label: string;
  /** Optional description shown in the UI */
  description?: string;
  /** Field input type */
  fieldType: FieldType;
  /** Placeholder hint shown in empty inputs */
  placeholder?: string;
  /**
   * Advisory list of appointment types this field is relevant to. Omitted
   * means "applies to every appointment type" (the default). Surfaced by
   * the Per-Case Fields settings tab (grouping) and the template field
   * picker (suggestion filtering). NOT a runtime gate — an existing
   * template referencing a field outside its applicability keeps
   * rendering exactly as before. See MOR-429.
   */
  appointmentTypes?: AppointmentType[];
}

export interface FieldCategory {
  /** Category identifier */
  id: string;
  /** Display name */
  label: string;
  /** Brief description */
  description: string;
  /** Fields in this category */
  fields: CanonicalField[];
}

// ---------------------------------------------------------------------------
// Canonical Field Categories
// ---------------------------------------------------------------------------

export const FIELD_CATEGORIES: FieldCategory[] = [
  // ── Practitioner Details ──────────────────────────────────────────────
  {
    id: "practitioner",
    label: "Practitioner Details",
    description: "Registered liquidator / insolvency practitioner information",
    fields: [
      {
        key: "practitioner_name",
        label: "Practitioner Name",
        description: "Full name of the registered liquidator",
        fieldType: "text",
        placeholder: "Morgan Liquidsworth",
      },
      // Salutation title (Mr/Mrs/Miss/Ms/Dr) — distinct from the
      // appointment-derived `practitioner_title` (Liquidator, Administrator,
      // …). Drives the Title checkbox rows on third-party forms via
      // checkbox groups linked to this key.
      {
        key: "practitioner_salutation",
        label: "Practitioner Title (Mr/Mrs/Ms)",
        description: "Salutation ticked on forms — Mr, Mrs, Miss, Ms, Dr, …",
        fieldType: "text",
        placeholder: "Mr",
      },
      // MOR-771 — ATO Incap form needs the practitioner name split into ATO
      // given/family parts. Derived on the fly from `practitioner_name`; see
      // `splitPractitionerName` in jobs/precedents/practitionerNameParts.ts.
      {
        key: "practitioner_first_given_name",
        label: "Practitioner First Given Name",
        description:
          "First given name, derived from Practitioner Name (MOR-771)",
        fieldType: "text",
      },
      {
        key: "practitioner_other_given_names",
        label: "Practitioner Other Given Names",
        description:
          "Middle / other given names, derived from Practitioner Name (MOR-771)",
        fieldType: "text",
      },
      {
        key: "practitioner_family_name",
        label: "Practitioner Family Name",
        description:
          "Family name (surname), derived from Practitioner Name (MOR-771)",
        fieldType: "text",
      },
      {
        key: "practitioner_number",
        label: "Registered Liquidator Number",
        description: "ASIC registered liquidator number",
        fieldType: "text",
        placeholder: "123456",
      },
      {
        key: "practitioner_email",
        label: "Practitioner Email",
        fieldType: "email",
        placeholder: "morgan@windingdown.com.au",
      },
      {
        key: "practitioner_phone",
        label: "Practitioner Phone",
        fieldType: "phone",
        placeholder: "(02) 0000 0000",
      },
    ],
  },

  // ── Firm Details ──────────────────────────────────────────────────────
  {
    id: "firm",
    label: "Firm Details",
    description: "Insolvency practice / firm information",
    fields: [
      {
        key: "firm_name",
        label: "Firm Name",
        description: "Practice entity name",
        fieldType: "text",
        placeholder: "Morgan & Associates Pty Ltd",
      },
      {
        key: "firm_abn",
        label: "Firm ABN",
        fieldType: "text",
        placeholder: "00 000 000 000",
      },
      {
        key: "firm_address",
        label: "Firm Address",
        description: "Full street address",
        fieldType: "address",
        placeholder: "Level 42, 1 Liquidation Lane",
      },
      {
        key: "firm_street",
        label: "Firm Street",
        fieldType: "text",
        placeholder: "Level 42, 1 Liquidation Lane",
      },
      {
        key: "firm_suburb",
        label: "Firm Suburb/City",
        fieldType: "text",
        placeholder: "Solvencyville",
      },
      {
        key: "firm_state",
        label: "Firm State",
        fieldType: "text",
        placeholder: "NSW",
      },
      {
        key: "firm_postcode",
        label: "Firm Postcode",
        fieldType: "text",
        placeholder: "2000",
      },
      {
        key: "firm_phone",
        label: "Firm Phone",
        fieldType: "phone",
        placeholder: "(02) 0000 0000",
      },
      {
        key: "firm_email",
        label: "Firm Email",
        fieldType: "email",
        placeholder: "hello@morganassociates.com.au",
      },
    ],
  },

  // ── Company Details ───────────────────────────────────────────────────
  {
    id: "company",
    label: "Company Details",
    description:
      "Details of the company under administration (populated per-case)",
    fields: [
      {
        key: "company_name",
        label: "Company Name",
        description: "Select company names and additional names",
        fieldType: "text",
      },
      {
        key: "appointment_name",
        label: "Appointment Name",
        description:
          "The name the appointment is known by — the company on a corporate appointment, the individual's name on a bankruptcy (MOR-2342)",
        fieldType: "text",
      },
      {
        key: "bankrupt_estate_name",
        label: "Bankrupt Estate Name",
        description:
          'The estate heading from Job Settings, e.g. "Bankrupt Estate of …". Use only where the estate rather than the person is addressed (MOR-2342)',
        fieldType: "text",
        appointmentTypes: [
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
          "debtors_petition",
          "part_x_arrangement",
        ],
      },
      {
        key: "company_name_variant_lines",
        label: "Additional Company Name Lines",
        description:
          "Selected trading, trust, and former trading names for this job",
        fieldType: "text",
      },
      {
        key: "trading_name_lines",
        label: "Trading Name Lines",
        description: "Selected trading names for this job",
        fieldType: "text",
      },
      {
        key: "trust_name_lines",
        label: "Trust Name Lines",
        description: "Selected trust names for this job",
        fieldType: "text",
      },
      {
        key: "former_trading_name_lines",
        label: "Former Trading Name Lines",
        description: "Selected former trading names for this job",
        fieldType: "text",
      },
      {
        key: "company_status",
        label: "Company Status",
        description: "e.g. In Liquidation",
        fieldType: "text",
      },
      {
        key: "acn",
        label: "ACN",
        description: "Australian Company Number",
        fieldType: "text",
      },
      {
        key: "abn",
        label: "ABN",
        description:
          "Australian Business Number (company's own ABN — blank if company only acts as trustee)",
        fieldType: "text",
      },
      // MOR-340 — trust handling. Separate fields so precedent templates
      // can render the trust ABN where the operating business sat under a
      // trust, and so the dual-capacity signal (`trustee_capacity`) is
      // surfaced for reviewers.
      {
        key: "trust_abn",
        label: "Trust ABN",
        description:
          "ABN of the trust the company acts as trustee for (if any)",
        fieldType: "text",
      },
      {
        key: "trust_name",
        label: "Trust Name",
        description: "Entity name of the trust per ABR",
        fieldType: "text",
      },
      {
        key: "trustee_capacity",
        label: "Trustee Capacity",
        description:
          'Renders as "Sole Trustee", "Dual Capacity", or "Unknown" — flags whether the company also traded in its own capacity',
        fieldType: "text",
      },
      {
        key: "tfn",
        label: "TFN",
        description: "Tax File Number",
        fieldType: "text",
      },
      {
        key: "industry_division",
        label: "Industry Division",
        description: "ANZSIC industry division",
        fieldType: "text",
      },
      {
        key: "industry_group",
        label: "Industry Group",
        description: "ANZSIC industry group",
        fieldType: "text",
      },
      { key: "anzsic_code", label: "ANZSIC Code", fieldType: "text" },
      {
        key: "background_summary",
        label: "Background Summary",
        description:
          "Background of the enterprise — the AI-drafted, practitioner-editable summary from the job overview page. Multi-paragraph plain text.",
        fieldType: "text",
      },
      {
        key: "registered_office_street",
        label: "Registered Office — Street",
        fieldType: "text",
      },
      {
        key: "registered_office_suburb",
        label: "Registered Office — Suburb",
        fieldType: "text",
      },
      {
        key: "registered_office_state",
        label: "Registered Office — State",
        fieldType: "text",
      },
      {
        key: "registered_office_postcode",
        label: "Registered Office — Postcode",
        fieldType: "text",
      },
      {
        key: "registered_office_address",
        label: "Registered Office — Full Address",
        description:
          "Composed single-line registered office address (street, suburb, state, postcode)",
        fieldType: "address",
      },
      {
        key: "principal_place_street",
        label: "Principal Place of Business — Street",
        fieldType: "text",
      },
      {
        key: "principal_place_suburb",
        label: "Principal Place of Business — Suburb",
        fieldType: "text",
      },
      {
        key: "principal_place_state",
        label: "Principal Place of Business — State",
        fieldType: "text",
      },
      {
        key: "principal_place_postcode",
        label: "Principal Place of Business — Postcode",
        fieldType: "text",
      },
      {
        key: "principal_place_address",
        label: "Principal Place of Business — Full Address",
        description:
          "Composed single-line principal place of business address (street, suburb, state, postcode)",
        fieldType: "address",
      },
      {
        key: "additional_place_of_business_lines",
        label: "Additional Places of Business",
        description:
          "Known trading addresses not recorded on the company search, one per line",
        fieldType: "text",
      },
      {
        key: "date_commenced_trading",
        label: "Date Commenced Trading",
        fieldType: "date",
      },
      {
        key: "date_ceased_trading",
        label: "Date Ceased Trading",
        fieldType: "date",
      },
    ],
  },

  // ── Job Bank Account ──────────────────────────────────────────────────
  // MOR-770 — estate/administration bank account opened for the job.
  // Entered manually in Job Settings → Job Details; pulled through to
  // documents that ask where funds should be remitted.
  {
    id: "bank",
    label: "Job Bank Account",
    description:
      "Bank account opened for this appointment (populated per-case from Job Settings)",
    fields: [
      {
        key: "job_bank_name",
        label: "Job Bank — Bank Name",
        description: "Financial institution the job's account is held with",
        fieldType: "text",
      },
      {
        key: "job_bank_account_name",
        label: "Job Bank — Account Name",
        description: "Name the job's account is held in",
        fieldType: "text",
      },
      {
        key: "job_bank_bsb",
        label: "Job Bank — BSB",
        description: "Format: 000-000",
        fieldType: "text",
      },
      {
        key: "job_bank_account_number",
        label: "Job Bank — Account Number",
        fieldType: "text",
      },
      {
        key: "job_bank_details",
        label: "Job Bank — Full Details",
        description:
          "Composed block: bank, account name, BSB, account number — one per line",
        fieldType: "text",
      },
    ],
  },

  // ── Contact Information ───────────────────────────────────────────────
  {
    id: "contact",
    label: "Contact Information",
    description: "Contact person details used in correspondence",
    fields: [
      {
        key: "contact_name",
        label: "Contact Person Name",
        description: "Person handling day-to-day queries",
        fieldType: "text",
        placeholder: "Penny Receivership",
      },
      {
        key: "contact_salutation",
        label: "Contact Person Title (Mr/Mrs/Ms)",
        description: "Salutation ticked on forms — Mr, Mrs, Miss, Ms, Dr, …",
        fieldType: "text",
        placeholder: "Ms",
      },
      {
        key: "contact_email",
        label: "Contact Email",
        fieldType: "email",
        placeholder: "penny@morganassociates.com.au",
      },
      {
        key: "contact_phone",
        label: "Contact Phone",
        fieldType: "phone",
        placeholder: "(02) 0000 0001",
      },
    ],
  },

  // ── Appointment Details ───────────────────────────────────────────────
  {
    id: "appointment",
    label: "Appointment Details",
    description: "Details of the insolvency appointment (populated per-case)",
    fields: [
      {
        key: "appointment_date",
        label: "Date of Appointment",
        fieldType: "date",
      },
      // MOR-901 — the appointment this one succeeded, from the job's
      // appointment history. Auto-derived when the appointment type is changed
      // via Record Change of Appointment; blank on a first appointment.
      {
        key: "prior_appointment_type",
        label: "Prior Appointment Type",
        description:
          'The appointment this one replaced, e.g. "Voluntary Administration" on a liquidation that followed an administration. Auto-derived from the job\'s appointment history — blank where this is the first appointment.',
        fieldType: "text",
      },
      {
        key: "prior_appointment_date",
        label: "Prior Appointment Date",
        description:
          "Date the prior appointment took effect, e.g. the date the administrators were appointed. Auto-derived from the job's appointment history.",
        fieldType: "date",
      },
      {
        key: "liquidation_date",
        label: "Liquidation Start Date",
        description:
          "Day the winding up began. Auto-filled with the appointment date when an administration converts to a liquidation; also the date the FEG service calculation measures employee service to where the administration did not run continuously.",
        fieldType: "date",
        appointmentTypes: [
          "cvl",
          "court_liquidation",
          "simplified_liquidation",
          "mvl",
        ],
      },
      {
        key: "practitioner_title",
        label: "Practitioner Title",
        description:
          "Professional title — varies by appointment type (e.g. Liquidator, Administrator)",
        fieldType: "text",
      },
      {
        key: "second_practitioner_name",
        label: "Second Practitioner Name",
        description: "Joint appointee name (if applicable)",
        fieldType: "text",
      },
      // MOR-771 — ATO Incap "Representative 2" needs the joint appointee name
      // split into ATO given/family parts. Derived from `second_practitioner_name`.
      {
        key: "second_practitioner_first_given_name",
        label: "Second Practitioner First Given Name",
        description:
          "First given name, derived from Second Practitioner Name (MOR-771)",
        fieldType: "text",
      },
      {
        key: "second_practitioner_other_given_names",
        label: "Second Practitioner Other Given Names",
        description:
          "Middle / other given names, derived from Second Practitioner Name (MOR-771)",
        fieldType: "text",
      },
      {
        key: "second_practitioner_family_name",
        label: "Second Practitioner Family Name",
        description:
          "Family name (surname), derived from Second Practitioner Name (MOR-771)",
        fieldType: "text",
      },
      {
        key: "second_practitioner_number",
        label: "Second Practitioner Number",
        description: "Joint appointee ASIC number",
        fieldType: "text",
      },
      {
        key: "administrator_type",
        label: "Type of Administrator",
        description: "e.g. Liquidator of Court liquidation",
        fieldType: "text",
      },
      {
        key: "method_of_appointment",
        label: "Method of Appointment",
        description: "e.g. appointment by court order",
        fieldType: "text",
      },
      {
        key: "appointment_type",
        label: "Appointment Type Checkbox",
        description: "Which appointment type applies (ROCAP)",
        fieldType: "text",
      },
      {
        key: "date_person_took_control",
        label: "Date Person Took Control",
        description: "Managing Controller only",
        fieldType: "date",
      },
      {
        key: "date_received_report",
        label: "Date ROCAP Issued",
        fieldType: "date",
      },
      {
        key: "report_return_date",
        label: "Date ROCAP is Due from Director",
        fieldType: "date",
      },
      {
        key: "records_period_from",
        label: "Records Period — From",
        fieldType: "date",
      },
      {
        key: "records_period_to",
        label: "Records Period — To",
        fieldType: "date",
      },
      {
        key: "signatory_name",
        label: "Signatory Name",
        description: "Printed name below signature — changes per job",
        fieldType: "text",
        placeholder: "Morgan Liquidsworth",
      },
      {
        key: "signatory_title",
        label: "Signatory Title",
        description: "Title shown below signature — varies by appointment type",
        fieldType: "text",
        placeholder: "Liquidator",
      },
      {
        key: "submitted_name",
        label: "Submitted By — Name",
        fieldType: "text",
      },
    ],
  },

  // ── Court / Regulatory ────────────────────────────────────────────────
  {
    id: "court",
    label: "Court / Regulatory",
    description: "Court and regulatory body details (populated per-case)",
    fields: [
      {
        key: "court_name",
        label: "Court Name",
        description: "e.g. Supreme Court, Federal Court",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "provisional_liquidation",
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "court_state",
        label: "Court State/Territory",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "provisional_liquidation",
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "court_order_date",
        label: "Date of Court Order",
        fieldType: "date",
        appointmentTypes: [
          "court_liquidation",
          "provisional_liquidation",
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
        ],
      },
      // MOR-901 — stays court-liquidation only here. On a VA (and on a
      // liquidation that followed one) the same date is captured by the Prior
      // Court Application block instead, so widening this would render two
      // inputs for one field.
      {
        key: "winding_up_application_date",
        label: "Date Winding-up Application Filed",
        description:
          "Date the application for winding up was filed (per ASIC notice) — drives the Court Liquidation relation-back day. Earlier than the court order date.",
        fieldType: "date",
        appointmentTypes: ["court_liquidation"],
      },
      // MOR-813 — the s513C day. Only set where the appointment followed a VA.
      // MOR-901 added `deed_of_company_arrangement`: a deed's relation-back day
      // traces to the administration too, and the date is now auto-carried at
      // the VA→DOCA step so it survives into a later liquidation.
      {
        key: "administration_start_date",
        label: "Administration Start Date",
        description:
          "Day the first administrator was appointed under Part 5.3A (the s513C day), where this appointment followed a voluntary administration — the relation-back day then traces to it. A failed deed of company arrangement traces back to the ORIGINAL administration, not the deed's execution or termination date. Leave blank when no administration preceded the appointment.",
        fieldType: "date",
        appointmentTypes: [
          "cvl",
          "court_liquidation",
          "simplified_liquidation",
          "deed_of_company_arrangement",
        ],
      },
      {
        key: "court_number",
        label: "Court / Matter Number",
        description:
          "Full identifier as printed, e.g. 2025/00425433 or SYG2872/2025",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "provisional_liquidation",
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "sequestration_number",
        label: "Sequestration / File Number",
        description: "Numeric/case-id portion only, e.g. 425433 or SYG2872",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "personal_bankruptcy",
          "creditors_petition",
        ],
      },
      // MOR-772 — ATO Incap captures the sequestration year in its own comb
      // boxes, separate from the file number. Mirrors `proceeding_year` (the
      // year portion of the court number) so it auto-fills from the job.
      {
        key: "sequestration_year",
        label: "Sequestration / File Year",
        description:
          "Year portion of the sequestration/court number, e.g. 2025 (MOR-772)",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "personal_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "proceeding_matter_number",
        label: "Proceeding Matter Number",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "provisional_liquidation",
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "proceeding_year",
        label: "Proceeding Year",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "provisional_liquidation",
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "plaintiff_name",
        label: "Plaintiff / Petitioning Creditor",
        description:
          "First plaintiff (Court Liq) or applicant creditor (BKY) — preserve original case",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "personal_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "defendant_name",
        label: "First Defendant",
        description: "First defendant on a Court Liq — preserve original case",
        fieldType: "text",
        appointmentTypes: ["court_liquidation"],
      },
      {
        key: "bankrupt_name",
        label: "Bankrupt Name",
        description: "Respondent / debtor — preserve original case",
        fieldType: "text",
        appointmentTypes: [
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
          "debtors_petition",
          "part_x_arrangement",
        ],
      },
      {
        key: "bankrupt_date_of_birth",
        label: "Bankrupt Date of Birth",
        description:
          "MOR-1847 — the individual's date of birth; identifies them on the personal conflict check",
        fieldType: "date",
        appointmentTypes: [
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
          "debtors_petition",
          "part_x_arrangement",
        ],
      },
      {
        key: "administration_number",
        label: "Administration Number / Bankrupt Estate",
        description: "AFSA administration number, e.g. NSW 1262 of 2025/3",
        fieldType: "text",
        appointmentTypes: [
          "personal_bankruptcy",
          "business_bankruptcy",
          "creditors_petition",
          "debtors_petition",
          "part_x_arrangement",
        ],
      },
      {
        key: "petitioning_creditor_costs",
        label: "Petitioning Creditor Costs",
        description:
          "Costs awarded to the plaintiff / applicant creditor, in AUD",
        fieldType: "text",
        appointmentTypes: [
          "court_liquidation",
          "personal_bankruptcy",
          "creditors_petition",
        ],
      },
      {
        key: "act_of_bankruptcy_date",
        label: "Date of Act of Bankruptcy",
        description: "Drives the BKY relation-back day",
        fieldType: "date",
        appointmentTypes: ["personal_bankruptcy", "creditors_petition"],
      },
      {
        key: "court_file_reference",
        label: "Court File Reference",
        description:
          'MOR-470 — composed ACN-line substitute, e.g. "In the Federal Circuit and Family Court of Australia File No: SYG1316/2025". Auto-derived from court name + file number.',
        fieldType: "text",
        appointmentTypes: [
          "personal_bankruptcy",
          "business_bankruptcy",
          "court_liquidation",
          "creditors_petition",
        ],
      },
    ],
  },

  // ── Meeting of Creditors ──────────────────────────────────────────────
  {
    id: "meeting",
    label: "Meeting of Creditors",
    description: "Details of a meeting of creditors (populated per-case)",
    fields: [
      {
        key: "meeting_date",
        label: "Meeting Date",
        description: "Date the meeting of creditors is held",
        fieldType: "date",
      },
      {
        key: "meeting_time",
        label: "Meeting Time",
        description: "Start time including timezone, e.g. 2:00PM AEST",
        fieldType: "text",
      },
      {
        key: "meeting_location",
        label: "Meeting Location",
        description: "Physical venue of the meeting",
        fieldType: "address",
      },
      {
        key: "meeting_virtual_details",
        label: "Meeting Virtual Access Details",
        description: "Video conference / teleconference joining details",
        fieldType: "text",
      },
      {
        key: "proxy_return_date",
        label: "Proxy / Proof of Debt Return Date",
        description:
          "Date by which proofs of debt and proxy forms must be returned",
        fieldType: "date",
      },
      {
        key: "proxy_return_time",
        label: "Proxy / Proof of Debt Return Time",
        description:
          "Time by which proofs of debt and proxy forms must be returned, e.g. 4:00PM AEST",
        fieldType: "text",
      },
    ],
  },

  // ── Director Details ──────────────────────────────────────────────────
  {
    id: "director",
    label: "Director Details",
    description: "Director information (populated per-case)",
    fields: [
      { key: "director_name", label: "Director Name", fieldType: "text" },
      {
        key: "director_email",
        label: "Director Email",
        description: "Director's email address (copy to)",
        fieldType: "text",
      },
      {
        key: "director_address",
        label: "Director Address",
        fieldType: "address",
      },
    ],
  },

  // ── Recipient Details ─────────────────────────────────────────────────
  {
    id: "recipient",
    label: "Recipient Details",
    description: "Letter recipient information (populated per-case)",
    fields: [
      {
        key: "recipient_name",
        label: "Recipient Name / Organisation",
        fieldType: "text",
      },
      {
        key: "recipient_address",
        label: "Recipient Address",
        fieldType: "address",
      },
      { key: "recipient_email", label: "Recipient Email", fieldType: "email" },
    ],
  },

  // ── Third-Party Accounts ──────────────────────────────────────────────
  {
    id: "third_party",
    label: "Third-Party Accounts",
    description:
      "Account holder details for Xero, MYOB, etc. (populated per-case)",
    fields: [
      {
        key: "account_holder_name",
        label: "Account Holder Name",
        description: "Current subscription/account holder",
        fieldType: "text",
      },
      {
        key: "account_holder_email",
        label: "Account Holder Email",
        fieldType: "email",
      },
      // MOR-706 — the MYOB access form asks for the business owner separately
      // from the subscription/account holder, so they need their own keys.
      {
        key: "business_owner_name",
        label: "Business Owner Name",
        description:
          "Business owner named on the access form (may differ from the account holder)",
        fieldType: "text",
      },
      {
        key: "business_owner_email",
        label: "Business Owner Email",
        fieldType: "email",
      },
      {
        key: "xero_organisation",
        label: "Xero Organisation Name",
        fieldType: "text",
      },
      {
        key: "insolvency_type",
        label: "Insolvency Type",
        description: "e.g. Liquidation, Voluntary Administration",
        fieldType: "text",
      },
      {
        key: "request_type",
        label: "Request Type",
        description: "e.g. Subscriber access, Transfer subscription",
        fieldType: "text",
      },
      {
        key: "myob_serial_number",
        label: "MYOB Serial Number",
        fieldType: "text",
      },
      {
        key: "myob_account_code",
        label: "MYOB Account Code",
        fieldType: "text",
      },
      {
        key: "myob_add_online_owner",
        label: "Add Practitioner as Online Owner",
        description: "MYOB: Add insolvency practitioner to file",
        fieldType: "checkbox",
      },
      {
        key: "myob_remove_admin_password",
        label: "Remove Administrator Password",
        description: "MYOB: Remove admin password from file",
        fieldType: "checkbox",
      },
      {
        key: "myob_remove_former_users",
        label: "Remove Former Business Users",
        description: "MYOB: Remove former users from file",
        fieldType: "checkbox",
      },
      {
        key: "myob_years_access",
        label: "Years of Access Required",
        description: "Number of years access required (up to 7)",
        fieldType: "number",
      },
      {
        key: "accounting_practice",
        label: "Accounting Practice Name",
        fieldType: "text",
      },
    ],
  },

  // ── Signature / Authentication ────────────────────────────────────────
  {
    id: "signature",
    label: "Signature / Authentication",
    description: "Signature and submission fields",
    fields: [
      { key: "signature", label: "Signature", fieldType: "signature" },
      { key: "signature_date", label: "Signature Date", fieldType: "date" },
      { key: "submission_date", label: "Submission Date", fieldType: "date" },
    ],
  },

  // ── Letter / Document ─────────────────────────────────────────────────
  {
    id: "document",
    label: "Letter / Document",
    description: "General document fields",
    fields: [
      { key: "letter_date", label: "Letter Date", fieldType: "date" },
      {
        key: "job_code",
        label: "Job Code",
        description:
          'The firm\'s own matter reference for this job, e.g. J-0019 — set in Job Settings. Typically printed as the "Our Ref" line.',
        fieldType: "text",
        placeholder: "J-0019",
      },
      { key: "postal_address", label: "Postal Address", fieldType: "address" },
      { key: "state", label: "State / Territory", fieldType: "text" },
    ],
  },

  // ── Gallagher Insurance ─────────────────────────────────────────────
  {
    id: "gallagher",
    label: "Gallagher Insurance",
    description: "Default values for the Gallagher insurance notification form",
    fields: [
      {
        key: "trading_status",
        label: "Trading Status",
        description: "Trading / Not Trading / Ceased Trading",
        fieldType: "text",
        placeholder: "Unknown",
      },
      {
        key: "employing_status",
        label: "Employing Status",
        description: "Employing / Not Employing",
        fieldType: "text",
        placeholder: "Unknown",
      },
      {
        key: "locations_by_state",
        label: "Locations by State",
        description: "Comma-separated: ACT, NSW, NT, QLD, SA, TAS, VIC, WA, NZ",
        fieldType: "text",
      },
      {
        key: "num_employees",
        label: "Number of Employees or Contractors",
        fieldType: "text",
        placeholder: "TBA",
      },
      {
        key: "estimated_asset_value",
        label: "Estimated Asset Value",
        fieldType: "text",
        placeholder: "TBA",
      },
      {
        key: "estimated_appointment_period",
        label: "Estimated Appointment Period",
        fieldType: "text",
        placeholder: "TBA",
      },
      {
        key: "estimated_turnover",
        label: "Estimated Turnover During Appointment",
        fieldType: "text",
        placeholder: "TBA",
      },
      {
        key: "pre_appointment_insurer",
        label: "Pre Appointment Insurer or Broker",
        fieldType: "text",
        placeholder: "TBA",
      },
      {
        key: "pre_appointment_workcover",
        label: "Pre Appointment WorkCover Insurer",
        description: "Australia only",
        fieldType: "text",
        placeholder: "TBA",
      },
      {
        key: "crucial_compliance",
        label: "Crucial Compliance Requirement",
        description:
          "Do you require assistance with Health & Safety, Environmental &/or Workplace Relations? (Yes/No)",
        fieldType: "text",
        placeholder: "No",
      },
      {
        key: "mobile_phone",
        label: "Mobile Phone",
        description: "Mobile contact number for the notification",
        fieldType: "phone",
      },
      {
        key: "business_activities",
        label: "Business Activities Description",
        description:
          "Activities of the business/industry/occupation under appointment",
        fieldType: "text",
      },
      { key: "general_comments", label: "General Comments", fieldType: "text" },
      {
        key: "nominee_1_name",
        label: "Nominee 1 — Name",
        description: "Additional recipient of the insurance notification",
        fieldType: "text",
      },
      {
        key: "nominee_1_email",
        label: "Nominee 1 — Email",
        fieldType: "email",
      },
      { key: "nominee_2_name", label: "Nominee 2 — Name", fieldType: "text" },
      {
        key: "nominee_2_email",
        label: "Nominee 2 — Email",
        fieldType: "email",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/** Flat map of all canonical fields keyed by field key */
export const CANONICAL_FIELDS_MAP: Record<
  string,
  CanonicalField & { categoryId: string }
> = Object.fromEntries(
  FIELD_CATEGORIES.flatMap((cat) =>
    cat.fields.map((f) => [f.key, { ...f, categoryId: cat.id }]),
  ),
);

/** All canonical field keys as a Set for fast membership checks */
export const CANONICAL_FIELD_KEYS = new Set(Object.keys(CANONICAL_FIELDS_MAP));

/** Total number of canonical fields */
export const CANONICAL_FIELD_COUNT = CANONICAL_FIELD_KEYS.size;

/**
 * Categories whose fields are typically populated per-case (not user defaults).
 * The Fields settings tab will still show them but mark them as "per-case" so
 * users understand they can't set a meaningful default for these.
 */
export const PER_CASE_CATEGORIES = new Set([
  "company",
  "bank",
  "meeting",
  "director",
  "recipient",
  "third_party",
  "appointment",
  "court",
  "signature",
]);

/**
 * Categories whose fields are good candidates for user-defined defaults
 * (they stay the same across cases).
 */
export const DEFAULT_CATEGORIES = new Set([
  "practitioner",
  "firm",
  "contact",
  "document",
  "gallagher",
]);

/**
 * Canonical keys from OTHER categories that also appear on the Gallagher form.
 * The Gallagher preset UI shows these alongside gallagher-category fields so
 * users can override any Gallagher form value from a single place.
 * Fields left empty fall through to their source category preset or job data.
 */
export const GALLAGHER_CROSS_CATEGORY_KEYS: string[] = [
  "appointment_date",
  "firm_name",
  "practitioner_name",
  "contact_name",
  "contact_email",
  "contact_phone",
  "firm_phone",
  "administrator_type",
  "company_name",
  "acn",
  "industry_division",
  "registered_office_street",
];

/**
 * Role titles a firm may fix as a house style PER APPOINTMENT TYPE
 * (MOR-2354): WLP signs bankruptcy correspondence as "Trustee" while its
 * liquidations keep "Liquidator". The Practitioner preset stores them under
 * type-scoped keys (`signatory_title:personal_bankruptcy`, see
 * `convex/lib/titleScopes.ts`); `mergeFieldValues` applies only the scope
 * matching the job's appointment type. The legacy unscoped keys stay VALID so
 * an existing value is never dropped silently on save — but they are never
 * applied; the settings page asks the firm to assign or discard them.
 */
export const PRACTITIONER_CROSS_CATEGORY_KEYS: string[] = [
  ...SCOPED_TITLE_KEYS,
  ...TITLE_KEYS,
];

/**
 * Cross-category keys each defaultable category may hold in a preset, on top
 * of its own fields.
 */
export const CROSS_CATEGORY_KEYS_BY_CATEGORY: Record<string, string[]> = {
  gallagher: GALLAGHER_CROSS_CATEGORY_KEYS,
  practitioner: PRACTITIONER_CROSS_CATEGORY_KEYS,
};

/** Map of category ID → Set of valid field keys for that category. */
export const CATEGORY_KEY_SETS: Record<
  string,
  Set<string>
> = Object.fromEntries(
  FIELD_CATEGORIES.map((cat) => [
    cat.id,
    new Set(cat.fields.map((f) => f.key)),
  ]),
);

/**
 * Returns true when `field` should be shown for `appointmentType`. Fields
 * without an `appointmentTypes` array are universal and always return true,
 * as is the case when `appointmentType` itself is undefined (the caller has
 * no context, so we err toward showing). Used by the Per-Case Fields settings
 * tab and the template field picker to filter the candidate list. Advisory —
 * never used to block runtime substitution. See MOR-429.
 */
export function appointmentTypeAppliesToField(
  field: CanonicalField,
  appointmentType: AppointmentType | undefined,
): boolean {
  if (!field.appointmentTypes || field.appointmentTypes.length === 0) {
    return true;
  }
  if (!appointmentType) return true;
  return field.appointmentTypes.includes(appointmentType);
}

// ---------------------------------------------------------------------------
// Bankruptcy relabelling (MOR-563)
// ---------------------------------------------------------------------------

// On personal insolvency jobs the corporate-flavoured field sections
// ("Company Details", "Director Details") read confusingly, so the labels
// below are substituted at render time. Keys are never changed — templates
// keep rendering identically.

/** Category-label overrides applied for bankruptcy appointment types. */
const BANKRUPTCY_CATEGORY_LABELS: Record<string, string> = {
  company: "Bankrupt Details",
  director: "Bankrupt Contact Details",
};

/**
 * Field-label overrides for the most corporate-sounding identity fields so
 * they read correctly under the relabelled bankruptcy headings (the keys
 * stay `director_name` / `company_name` etc.).
 */
const BANKRUPTCY_FIELD_LABELS: Record<string, string> = {
  company_name: "Bankrupt Name",
  director_name: "Bankrupt Name",
  director_email: "Bankrupt Email",
  director_address: "Bankrupt Address",
};

/**
 * MOR-1838 — a Part X debtor is never made bankrupt, so the same fields
 * read "Debtor" rather than "Bankrupt".
 */
const DEBTOR_CATEGORY_LABELS: Record<string, string> = {
  company: "Debtor Details",
  director: "Debtor Contact Details",
};

const DEBTOR_FIELD_LABELS: Record<string, string> = {
  company_name: "Debtor Name",
  director_name: "Debtor Name",
  director_email: "Debtor Email",
  director_address: "Debtor Address",
  bankrupt_name: "Debtor Name",
  bankrupt_date_of_birth: "Debtor Date of Birth",
};

/** The label overrides for a personal insolvency type; null when corporate. */
function personalLabelOverrides(
  appointmentType: AppointmentType | undefined,
): { category: Record<string, string>; field: Record<string, string> } | null {
  if (!isPersonalInsolvencyAppointmentType(appointmentType)) return null;
  return isBankruptcyAppointmentType(appointmentType)
    ? { category: BANKRUPTCY_CATEGORY_LABELS, field: BANKRUPTCY_FIELD_LABELS }
    : { category: DEBTOR_CATEGORY_LABELS, field: DEBTOR_FIELD_LABELS };
}

/**
 * Resolve a category's display label for a given appointment type. Returns
 * a bankrupt/debtor-specific label for the `company` / `director`
 * categories on personal insolvency jobs; otherwise the default label.
 */
export function resolveCategoryLabel(
  categoryId: string,
  defaultLabel: string,
  appointmentType: AppointmentType | undefined,
): string {
  return (
    personalLabelOverrides(appointmentType)?.category[categoryId] ??
    defaultLabel
  );
}

/**
 * Resolve a field's display label for a given appointment type. Returns a
 * bankrupt/debtor-specific label for select identity fields on personal
 * insolvency jobs; otherwise the default label unchanged.
 */
export function resolveFieldLabel(
  fieldKey: string,
  defaultLabel: string,
  appointmentType: AppointmentType | undefined,
): string {
  return (
    personalLabelOverrides(appointmentType)?.field[fieldKey] ?? defaultLabel
  );
}

/**
 * Returns valid keys for a category, including cross-category keys for
 * categories that explicitly support them (e.g. Gallagher).
 *
 * Pass `customKeys` to extend validation with org-scoped custom field keys
 * that aren't part of the static canonical set.
 */
export function getValidKeysForCategory(
  categoryId: string,
  customKeys?: Set<string>,
): Set<string> {
  const own = CATEGORY_KEY_SETS[categoryId];
  if (!own && !customKeys?.size) return new Set();

  const merged = new Set(own ?? []);
  for (const k of CROSS_CATEGORY_KEYS_BY_CATEGORY[categoryId] ?? []) {
    merged.add(k);
  }
  if (customKeys) {
    for (const k of customKeys) merged.add(k);
  }
  return merged;
}
